import * as React from "react";
import { Badge } from "@/components/ui/badge";

interface RevenueItem {
  name: string;
  value: number;
  count: number;
  color?: string;
}

interface HorizontalRevenueBarChartProps {
  data: RevenueItem[];
  total: number;
  barColor?: string;
  onItemClick?: (name: string) => void;
  selectedItem?: string | null;
}

const currency = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL"
});

export function HorizontalRevenueBarChart({ 
  data, 
  total,
  barColor = "hsl(var(--primary))",
  onItemClick,
  selectedItem
}: HorizontalRevenueBarChartProps) {
  const maxValue = Math.max(...data.map(d => d.value), 1);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        Nenhum dado disponível
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {data.map((item, idx) => {
        const percentage = total > 0 ? (item.value / total * 100) : 0;
        const barWidth = maxValue > 0 ? (item.value / maxValue * 100) : 0;
        const isSelected = selectedItem === item.name;
        const isDimmed = selectedItem != null && !isSelected;

        return (
          <div
            key={idx}
            className={`group rounded-lg transition-all duration-200 ${
              onItemClick ? 'cursor-pointer hover:bg-muted/60' : ''
            } ${isSelected ? 'bg-muted ring-2 ring-primary/40' : ''} ${
              isDimmed ? 'opacity-50' : ''
            }`}
            onClick={() => {
              if (onItemClick) {
                onItemClick(isSelected ? '' : item.name);
              }
            }}
          >
            {/* Row with all info */}
            <div className="flex items-center gap-3 px-1 py-0.5">
              {/* Name */}
              <div className="w-40 shrink-0 flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full shrink-0" 
                  style={{ backgroundColor: barColor }} 
                />
                <span className="text-sm font-medium truncate" title={item.name}>
                  {item.name}
                </span>
              </div>

              {/* Bar */}
              <div className="flex-1 h-8 bg-muted/50 rounded-lg overflow-hidden relative">
                <div
                  className="h-full rounded-lg transition-all duration-500"
                  style={{ 
                    width: `${barWidth}%`,
                    backgroundColor: barColor
                  }}
                />
              </div>

              {/* Value */}
              <div className="w-28 shrink-0 text-right">
                <span className="font-semibold text-sm">
                  {currency.format(item.value)}
                </span>
              </div>

              {/* Percentage */}
              <div className="w-16 shrink-0 text-right">
                <Badge variant="outline" className="text-xs">
                  {percentage.toFixed(1)}%
                </Badge>
              </div>

              {/* Count */}
              <div className="w-12 shrink-0 text-right">
                <span className="text-sm text-muted-foreground">
                  {item.count}x
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Total row */}
      {data.length > 0 && (
        <div className="flex items-center gap-3 px-1 py-2 border-t-2 border-border mt-2 pt-3">
          <div className="w-40 shrink-0 flex items-center gap-2">
            <div className="w-3 h-3 shrink-0" />
            <span className="text-sm font-bold">Total</span>
          </div>
          <div className="flex-1" />
          <div className="w-28 shrink-0 text-right">
            <span className="font-bold text-sm">{currency.format(total)}</span>
          </div>
          <div className="w-16 shrink-0 text-right">
            <Badge variant="outline" className="text-xs font-bold">100%</Badge>
          </div>
          <div className="w-12 shrink-0 text-right">
            <span className="text-sm font-bold text-muted-foreground">
              {data.reduce((sum, d) => sum + d.count, 0)}x
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
